import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '@/lib/api/client';
import { objectsByTypeQueryKey, queryKeys } from '@/lib/query/keys';
import type { BackendObjectType } from '@/types/objectTypes';
import { useDemoMode, useDemoFilteredResult } from '@/features/demo/useDemoMode';
import { useSession } from '@/features/auth/hooks/useSession';

// Re-export so existing consumers (PlaceListModule, tests) keep their import
// path; the builder itself is shared with hooks/queries.ts via lib/query/keys
// so both useObjectsByType families key congruently (#5259).
export { objectsByTypeQueryKey, type ObjectsByTypeQueryKeyParams } from '@/lib/query/keys';
export interface XaviorObjectBase {
  id: string;
  type: BackendObjectType;
  createdAt: string;
  updatedAt: string;
  version: number;
  _etag?: string;
}

type ListResponse<T> = {
  items?: T[];
  nextCursor?: string;
};

export function useObjectsByType<T extends XaviorObjectBase>(
  type: BackendObjectType,
  params: { conversationId?: string; moduleId?: string; limit?: number; query?: string; includeArchived?: boolean; enabled?: boolean; startAfter?: string; startBefore?: string; expandRecurring?: boolean; includeDriveCache?: boolean } = {},
) {
  const { moduleId, limit = 50, query, includeArchived, enabled, startAfter, startBefore, expandRecurring, includeDriveCache } = params;
  const { filterItems } = useDemoMode();
  const { token } = useSession();

  // #2725: Events refetch eagerly so calendar-sync updates surface quickly,
  // but we KEEP prior data on screen during refetches so the UI never flashes
  // a "Loading events..." placeholder. Disabling gcTime entirely (the previous
  // behavior) tore the cache down on every remount which both forced the
  // loading state to flash AND aggravated the stale-mapping reports in #1885
  // — without a cache there was no place for an invalidation to land, so the
  // next render briefly showed nothing.
  const isEvent = type === 'event';

  const result = useQuery({
    queryKey: objectsByTypeQueryKey(type, params),
    // #5586: gate on a token so an expired/absent session doesn't keep firing
    // (and freezing on) a query that can never succeed — mirrors the
    // hooks/queries.ts family's `enabled: !!token`.
    enabled: (enabled !== false) && Boolean(token),
    // Stale-while-revalidate: keep previous data on screen during refetches so
    // the calendar/list never flashes an empty "Loading events..." state.
    placeholderData: (previousData) => previousData,
    staleTime: isEvent ? 0 : undefined,
    // Keep events cached for 5 minutes so re-mounts don't tear the list down.
    gcTime: isEvent ? 5 * 60_000 : undefined,
    refetchOnMount: isEvent ? 'always' : true,
    queryFn: async () => {
      const search = new URLSearchParams();
      search.set('limit', String(limit));
      if (moduleId) {
        search.set('moduleIds', moduleId);
      }
      if (query) search.set('query', query);
      if (includeArchived) search.set('includeArchived', 'true');
      // Surface Drive/OneDrive-cached rows (Picker-ingested files) that the
      // server excludes from /notes,/assets by default. Drive module opts in.
      if (includeDriveCache) search.set('includeDriveCache', 'true');
      if (startAfter) search.set('startAfter', startAfter);
      if (startBefore) search.set('startBefore', startBefore);
      // Opt out of server-side recurrence expansion — this consumer expands
      // client-side (with exception handling). Default stays server-expanded.
      if (expandRecurring === false) search.set('expandRecurring', 'false');

      const { data } = await apiGet<ListResponse<T>>(`/${type}s?${search.toString()}`);
      const items = (data.items ?? []).map(item => {
        const preservedEtag = (item as any)._etag;
        const rawConversationLinks =
          (item as any).conversationLinks
          || (item as any).conversation_links
          || (item as any).data?.conversationLinks
          || (item as any).attributes?.conversationLinks
          || [];
        const normalizedConversationLinks = Array.isArray(rawConversationLinks)
          ? rawConversationLinks
            .map((entry: any) => {
              if (typeof entry === 'string') return { conversationId: entry };
              if (entry && typeof entry === 'object') {
                return {
                  conversationId:
                    entry.conversationId
                    || entry.conversation_id
                    || entry.id,
                };
              }
              return null;
            })
            .filter((entry: any) => Boolean(entry?.conversationId))
          : [];

        const rawModuleLinks =
          (item as any).moduleLinks
          || (item as any).module_links
          || (item as any).data?.moduleLinks
          || (item as any).attributes?.moduleLinks
          || [];
        const normalizedModuleLinks = Array.isArray(rawModuleLinks)
          ? rawModuleLinks
            .map((entry: any) => {
              if (typeof entry === 'string') return { moduleId: entry };
              if (entry && typeof entry === 'object') {
                return {
                  moduleId: entry.moduleId || entry.module_id || entry.id,
                };
              }
              return null;
            })
            .filter((entry: any) => Boolean(entry?.moduleId))
          : [];

        const normalizedConversationIds = Array.from(new Set(
          [
            (item as any).conversationId,
            ...((item as any).conversationIds || []),
            ...((item as any).conversation_ids || []),
            ...normalizedConversationLinks.map((entry: any) => entry.conversationId),
          ]
            .map((entry) => String(entry ?? '').trim())
            .filter(Boolean),
        ));

        const normalizedModuleIds = Array.from(new Set(
          [
            (item as any).moduleId,
            ...((item as any).moduleIds || []),
            ...((item as any).module_ids || []),
            ...((item as any).data?.moduleIds || []),
            ...((item as any).data?.module_ids || []),
            ...((item as any).attributes?.moduleIds || []),
            ...((item as any).attributes?.module_ids || []),
            ...normalizedModuleLinks.map((entry: any) => entry.moduleId),
          ]
            .map((entry) => String(entry ?? '').trim())
            .filter(Boolean),
        ));

        return {
          ...item,
          type,
          // Drive-sourced images carry only a Google `thumbnailLink` (CDN URL),
          // never a `thumbnailUrl`/`storageUrl`. The gallery + overlay read
          // `thumbnailUrl`, so surface the Drive thumbnail there (preserving any
          // real thumbnailUrl). Without this, Drive-sourced photos have no URL
          // for the gallery to render and show blank.
          thumbnailUrl:
            (item as any).thumbnailUrl
            ?? (item as any).thumbnailLink
            ?? (item as any).attributes?.thumbnailUrl
            ?? (item as any).attributes?.thumbnailLink,
          createdAt: (item as any).createdAt ?? (item as any).created_at,
          updatedAt: (item as any).updatedAt ?? (item as any).updated_at,
          conversationLinks: normalizedConversationLinks,
          conversation_links: normalizedConversationLinks,
          conversationIds: normalizedConversationIds,
          conversation_ids: normalizedConversationIds,
          moduleLinks: normalizedModuleLinks,
          module_links: normalizedModuleLinks,
          moduleIds: normalizedModuleIds,
          module_ids: normalizedModuleIds,
          // Only use item-specific ETag if present - do NOT use list ETag
          ...(preservedEtag && { _etag: preservedEtag }),
        };
      });
      return { ...data, items };
    },
  });

  return useDemoFilteredResult(result, filterItems);
}

export function useCreateObject<TPayload extends Record<string, any>, TObject extends XaviorObjectBase>(
  type: BackendObjectType,
  conversationId?: string,
  moduleId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TPayload & {
      conversationLinks?: { conversationId: string }[]
    }) => {
      const { data, etag } = await apiPost<TObject>(`/${type}s`, payload);
      return { ...data, _etag: etag };
    },
    onMutate: async (payload) => {
      const baseKey = queryKeys.objects.byType(type);
      await qc.cancelQueries({ queryKey: baseKey });

      const previousQueries: Array<{ key: any; data: any }> = [];
      const tempId = `optimistic-${Math.random().toString(36).slice(2)}`;

      // Normalize link fields the same way useObjectsByType does so consumers
      // that iterate conversationLinks/moduleIds don't receive undefined.
      const convLinks = Array.isArray(payload.conversationLinks) ? payload.conversationLinks : [];
      const modLinks = Array.isArray(payload.moduleLinks) ? payload.moduleLinks : [];
      const optimisticItem = {
        id: tempId,
        type,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: 0,
        ...payload,
        conversationLinks: convLinks,
        conversation_links: convLinks,
        conversationIds: convLinks.map((l: any) => l.conversationId).filter(Boolean),
        conversation_ids: convLinks.map((l: any) => l.conversationId).filter(Boolean),
        moduleLinks: modLinks,
        module_links: modLinks,
        moduleIds: modLinks.map((l: any) => l.moduleId).filter(Boolean),
        module_ids: modLinks.map((l: any) => l.moduleId).filter(Boolean),
      } as unknown as TObject;

      qc.getQueryCache().findAll({ queryKey: baseKey }).forEach((query) => {
        const oldData = query.state.data as { items?: TObject[] } | undefined;
        if (!oldData) return;

        // Scope to the creating module's cache only — prevents the placeholder
        // appearing in every other board/list of the same type simultaneously.
        if (moduleId && !query.queryKey.includes(`mod:${moduleId}`)) return;

        // Skip caches that already contain demo-sourced items: the demo filter
        // (useDemoFilteredResult) would immediately hide the non-demo placeholder,
        // causing a flash-then-vanish instead of the intended instant appearance.
        if (oldData.items?.some((item: any) => item.source?.startsWith?.('demo-'))) return;

        previousQueries.push({ key: query.queryKey, data: oldData });
        qc.setQueryData<{ items?: TObject[] }>(query.queryKey, {
          ...oldData,
          items: [optimisticItem, ...(oldData.items || [])],
        });
      });

      return { previousQueries, tempId };
    },
    onError: (_err, _input, context) => {
      context?.previousQueries.forEach(({ key, data }) => {
        qc.setQueryData(key, data);
      });
    },
    onSuccess: (data, _payload, context) => {
      // Swap the optimistic placeholder for the real server object
      const baseKey = queryKeys.objects.byType(type);
      if (context?.tempId) {
        qc.getQueryCache().findAll({ queryKey: baseKey }).forEach((query) => {
          const current = query.state.data as { items?: TObject[] } | undefined;
          if (current?.items) {
            qc.setQueryData<{ items?: TObject[] }>(query.queryKey, {
              ...current,
              items: current.items.map((item) => item.id === context.tempId ? data : item),
            });
          }
        });
      }
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: queryKeys.objects.all });
    },
  });
}

/**
 * Retry the failed outbound calendar push for a single event (#3951).
 *
 * POSTs /events/:id/retry-sync, which re-runs syncEventV2 server-side and
 * returns the re-stamped event (providerSyncState 'synced' on success, or
 * 'error' again with an updated providerSyncError). Invalidates the event lists
 * + the detail so the "not synced" badge reflects the new state. The response
 * already carries providerSyncState/providerSyncError at the top level
 * (mapObject spreads the event's attributes), so no extra transform is needed.
 */
export function useRetryEventSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { data, etag } = await apiPost<XaviorObjectBase & { providerSyncState?: string; providerSyncError?: string }>(
        `/events/${input.id}/retry-sync`,
        {},
      );
      return { ...data, _etag: etag };
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
      qc.invalidateQueries({ queryKey: queryKeys.objects.all });
      qc.invalidateQueries({ queryKey: queryKeys.objects.detail(input.id) });
    },
  });
}

export function useUpdateObject<TObject extends XaviorObjectBase>(type: BackendObjectType, conversationId?: string, moduleId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; etag: string; patch: Partial<TObject> }) => {
      const { id, etag, patch } = input;
      const { data, etag: newEtag } = await apiPatch<TObject>(`/${type}s/${id}`, patch, etag);
      return { ...data, _etag: newEtag ?? etag };
    },
    // Optimistic update before the API resolves — prevents kanban cards from
    // "snapping back" while dragging between columns.
    onMutate: async (input) => {
      const { id, patch } = input;

      const scopeKey = moduleId || conversationId;
      if (!scopeKey) return {};

      const baseKey = queryKeys.objects.byType(type);

      // Cancel outgoing refetches so they don't overwrite the optimistic update.
      await qc.cancelQueries({ queryKey: baseKey });

      const previousQueries: Array<{ key: any; data: any }> = [];

      qc.getQueryCache().findAll({ queryKey: baseKey }).forEach((query) => {
        const oldData = query.state.data as { items?: TObject[] } | undefined;
        if (oldData) {
          previousQueries.push({ key: query.queryKey, data: oldData });
          qc.setQueryData<{ items?: TObject[] }>(query.queryKey, {
            ...oldData,
            items: (oldData.items || []).map((item) =>
              item.id === id ? { ...item, ...patch } : item
            ),
          });
        }
      });

      return { previousQueries, baseKey };
    },
    onError: (_err, _input, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ key, data }) => {
          qc.setQueryData(key, data);
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.objects.byType(type) });
      qc.invalidateQueries({ queryKey: queryKeys.objects.all });
    },
    onSettled: (_data, _error, input) => {
      qc.invalidateQueries({ queryKey: queryKeys.objects.detail(input.id) });
    },
  });
}
